import { useCallback, useEffect, useState } from "react";
import { PushCategory, PushNotificationSettings } from "@scrile/api-provider/dist/api/PushNotificationsProvider";
import providers from "lib/providers";
import useAuthUser from "hooks/useAuthUser";

const ALL_CATEGORIES: PushCategory[] = [
  PushCategory.MESSAGES,
  PushCategory.CALLS,
  PushCategory.LIVECHATS,
  PushCategory.POSTS,
  PushCategory.REVIEWS,
];

export default function useController() {
  const { user } = useAuthUser();
  const [settings, setSettings] = useState<Partial<Record<PushCategory, boolean>>>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const loadSettings = useCallback(async () => {
    if (!user) return;
    try {
      const result: PushNotificationSettings = await providers.PushNotificationsProvider.settingsFind();
      const merged: Partial<Record<PushCategory, boolean>> = {};
      for (const cat of ALL_CATEGORIES) {
        merged[cat] = result.settings[cat] !== false;
      }
      setSettings(merged);
    } catch (e) {
      console.error("[NotificationSettings] Failed to load:", e);
      // Default all to enabled
      const defaults: Partial<Record<PushCategory, boolean>> = {};
      for (const cat of ALL_CATEGORIES) {
        defaults[cat] = true;
      }
      setSettings(defaults);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    loadSettings();
  }, [loadSettings]);

  const toggleCategory = useCallback(
    async (category: PushCategory) => {
      const newValue = !settings[category];
      const newSettings = { ...settings, [category]: newValue };
      setSettings(newSettings);

      setSaving(true);
      try {
        await providers.PushNotificationsProvider.settingsUpdate({
          data: { settings: { [category]: newValue } },
        });
      } catch (e) {
        console.error("[NotificationSettings] Failed to save:", e);
        // Revert on error
        setSettings(settings);
      } finally {
        setSaving(false);
      }
    },
    [settings]
  );

  return {
    categories: ALL_CATEGORIES,
    settings,
    loading,
    saving,
    toggleCategory,
  };
}
